/**
 * The hot tub in front of the juhlanavetta, the man in it, the kamiina that
 * heats it, the klapiteline beside it and the veräjä the klapi opens.
 *
 * All hashed: the tub's rim, Hannu's activation ring, the kamiina's pile and
 * hatch, and the gate and its post.
 */

import {
  cylinder,
  decor,
  decor as decor2,
  hose,
  solid,
  type CylinderSpec,
  type DecorSpec,
  type MaterialKey,
  type SolidSpec,
} from '../world/Level';
import { mulberry32 } from '../utils/prng';

/* ---------------------------------------------------------------- palju - */

/**
 * The hot tub, on the decking in front of the Juhlanavetta - which on the map
 * is exactly where the venue keeps it, and which is the whole reason Hannu is a
 * problem rather than scenery. He climbs out facing the door you are running
 * for, so his charge and your run line are the same line.
 *
 * The gap between the tub's south rim and the woodpile behind it is 2.25 m.
 * That is the fast way in, it is wide enough to run clean, and it is exactly
 * where Hannu lands. Going round the north side of the tub instead costs about
 * five metres and two corners - which is the trade the whole encounter is for.
 */
/**
 * Three metres further from the building than it used to be. The fence at the
 * top of its yard used to run *inside* the porstua's west wall, so the veräjä
 * and the door were the same place and the yard read as a corridor between two
 * walls rather than as somewhere a tub stands. The whole palju - staves,
 * kamiina, klapiteline, decking and fence - moved together; the halkopino did
 * not, because it is the yard's southern boundary and it has to keep reaching
 * the navetta's own south wall.
 *
 * What that opens up is the gravel apron between the veräjä and the porch,
 * which is where the pöytäryhmä now stands. See `PITKA_POYTA`.
 */
export const PALJU = { x: 119.0, z: -22.6, radius: 2.4, rim: 0.95 };

/**
 * The rim of a hot tub, as boxes.
 *
 * Sixteen of them round the ring rather than four walls in a square, and that
 * is not decoration. A tub is round; four walls make it a 5.5 m square whose
 * corners stand 3.9 m out from the centre against the staves' 2.75. The
 * north-east one of those corners is exactly the corner the fast line to the
 * veräjä turns, so a runner cutting past the tub was cutting past a metre of
 * collider that is not there.
 *
 * Each box is the bounding box of one 22.5 deg slice of the ring, grown by a
 * hair so consecutive slices overlap rather than merely touch - a ring with a
 * seam in it is a ring somebody eventually slips through. What is left is a
 * profile that never stands more than about 20 cm proud of the staves, which
 * at a run reads as round.
 */
const RIM_SEGMENTS = 16;

/**
 * How far the staves stand out past the water.
 *
 * It used to live inside `tubRim` as a bare `+ 0.35`, which was fine while the
 * ring was only something the player bumps into. Hannu keeps out of it now -
 * he climbs onto it and hops off it rather than walking through it - so the
 * number is the outside of the tub in two places at once and only gets to be
 * written down in one. See `HotTubSpec.rimRadius`.
 */
export const RIM_THICKNESS = 0.35;

export function tubRim(prefix: string, x: number, z: number, radius: number, height: number): SolidSpec[] {
  const outer = radius + RIM_THICKNESS;
  const grow = 0.02;
  const boxes: SolidSpec[] = [];
  for (let i = 0; i < RIM_SEGMENTS; i++) {
    const from = (i / RIM_SEGMENTS) * Math.PI * 2;
    const to = ((i + 1) / RIM_SEGMENTS) * Math.PI * 2;
    const xs: number[] = [];
    const zs: number[] = [];
    for (const angle of [from, to]) {
      for (const r of [radius, outer]) {
        xs.push(Math.cos(angle) * r);
        zs.push(Math.sin(angle) * r);
      }
    }
    boxes.push(
      solid(
        `${prefix}-reuna-${i + 1}`,
        'tub',
        x + Math.min(...xs) - grow,
        z + Math.min(...zs) - grow,
        x + Math.max(...xs) + grow,
        z + Math.max(...zs) + grow,
        height,
      ),
    );
  }
  return boxes;
}

/**
 * ==========================================================================
 *  THE KAMIINA, OUT OF THE WATER
 * ==========================================================================
 *  The stove that heats the palju, and the answer to why the water is hot at
 *  nine in the morning when nobody has been awake since four.
 *
 *  It used to stand *in* the tub, which is a way people do build them and
 *  which made a nonsense of the one line Hannu has: a man sitting in the water
 *  with the firebox at arm's length is not short of anything. So it stands
 *  where the other kind goes - on the decking off the tub's east end, on its
 *  own hearth slab, plumbed through the staves.
 *
 *  Two pipes, which is the whole of how a palju circulates and the reason the
 *  stove can be outside the water at all: cold water leaves the bottom of the
 *  tub, runs down to the base of the firebox, is heated, and rises back in
 *  near the surface. No pump - the temperature difference is the pump. Both
 *  arch over the rim rather than through it, because a hole through a stave is
 *  a hole in a tub.
 *
 *  ------------------------------------------------------------------------
 *   WHY IT IS ON *THIS* SIDE
 *  ------------------------------------------------------------------------
 *  East, between the tub and the veräjä, is the one place in the yard both
 *  ways past the palju come out at: the line along the halkopino and the line
 *  along the north rim converge in that strip, and a firebox that only one of
 *  them could reach would have quietly deleted the other. The klapikasa stays
 *  on the north rim where it always was, which is what makes the north side
 *  the line that opens the gate - you pass the logs on the way to the fire.
 *
 *  Unlike the old one this is a collider: it stands in the open, outside a rim
 *  that no longer covers it. The pipes, the hatch and the plume are not.
 * ==========================================================================
 */
export const KAMIINA = {
  /** On the decking off the tub's east end, a hand's width clear of the rim. */
  x: PALJU.x + 3.3,
  z: PALJU.z,
  radius: 0.42,
  /** Chest-high on a firebox you feed standing up. */
  height: 1.02,
  pipeRadius: 0.085,
  /**
   * Over the rim, over Hannu, and high enough that on the way across the yard
   * the smoke is the first thing you see of the palju.
   */
  pipeTop: 2.85,
  /** The hatch: narrow, because it is a plate laid flat across a curved face. */
  hatchWidth: 0.3,
  hatchBottom: 0.42,
  hatchHeight: 0.26,
  /** Where the two pipes leave it, either side of the hatch. */
  flowZ: PALJU.z + 0.32,
  returnZ: PALJU.z - 0.32,
} as const;

/** The hatch faces east, into the strip, which is the side you run past on. */
export const HATCH_X = KAMIINA.x + KAMIINA.radius;
export const HATCH_Z = KAMIINA.z - KAMIINA.hatchWidth / 2;
/** Middle of the hatch plate: what F is measured to, and where flames come out. */
export const HATCH_POINT = {
  x: HATCH_X + 0.06,
  y: KAMIINA.hatchBottom + KAMIINA.hatchHeight / 2,
  z: KAMIINA.z,
};

/** Where the water leaves the tub and where it comes back. See KAMIINA. */
const WATER_EDGE_X = PALJU.x + PALJU.radius;
export const KAMIINA_PIPES = [
  hose('kamiinan-menoputki', 'metal', 0.05, [
    { x: KAMIINA.x - 0.1, y: KAMIINA.height - 0.06, z: KAMIINA.flowZ },
    { x: KAMIINA.x - 0.5, y: KAMIINA.height + 0.24, z: KAMIINA.flowZ },
    { x: WATER_EDGE_X + 0.1, y: KAMIINA.height + 0.2, z: KAMIINA.flowZ },
    { x: WATER_EDGE_X - 0.25, y: 0.68, z: KAMIINA.flowZ },
  ]),
  hose('kamiinan-paluuputki', 'metal', 0.05, [
    { x: WATER_EDGE_X - 0.35, y: 0.14, z: KAMIINA.returnZ },
    { x: WATER_EDGE_X - 0.2, y: 0.6, z: KAMIINA.returnZ },
    { x: WATER_EDGE_X - 0.1, y: KAMIINA.height + 0.26, z: KAMIINA.returnZ },
    { x: KAMIINA.x - 0.45, y: KAMIINA.height + 0.3, z: KAMIINA.returnZ },
    { x: KAMIINA.x - 0.05, y: 0.3, z: KAMIINA.returnZ },
  ]),
];

/**
 * ==========================================================================
 *  THE VERÄJÄ, AND THE ONE WAY THROUGH IT
 * ==========================================================================
 *  The palju's yard is fenced off from the navetta's own front yard, and the
 *  veräjä is the walking route out of it eastwards:
 *
 *    the veräjä    z[-27.6..-25.0], on the fast line out of both the gate and
 *                  the strip, and SHUT until a pair of klapit is in the pesä
 *
 *  It used to be two. The boards stopped short of the kuusikko at the north
 *  end, and the hole that left was always open - three or four metres and two
 *  corners round the pöytäryhmä, and no toll. That gap is closed now: the
 *  fence runs on into the trees (`AIDAN_POHJOISPAA`), so the yard has one way
 *  out on foot and the klapit are what buy it. The solid kaljalava now also
 *  offers a two-jump shortcut over the boards with a running start.
 *  Go past the klapikasa on the tub's
 *  north side, pick the pair up on the way, put them through the hatch as you
 *  come down the strip, and out.
 *
 *  What is left to decline is the yard itself: the way round the outside of
 *  the kuusikko is still there and still finishes, and `Level.test.ts` drives
 *  it - it costs the length of the back field rather than two corners, which
 *  is the price of not paying the toll at all.
 *
 *  The fence uses the same boards as the yard's left-hand side, but is lower
 *  along its whole length so the shortcut has no visible notch. It is
 *  anchored the same way at both ends: north into `KUUSIKKO_PALJU`, south into
 *  the juhlanavetta's own front wall, so there is no end to walk round.
 * ==========================================================================
 */
export const VERAJA = {
  /** The fence line, and the leaf with it. */
  x: 125.9,
  /** Uniform across the tub yard's fence and gate, above a ground jump. */
  height: 1.15,
  /**
   * The opening, in world z, and 2.6 m of it - a farm gate a tractor goes
   * through, not a wicket.
   *
   * Where it is comes from what is on the other side. The porstua's west wall
   * runs out to z = -27.4 and the man asleep across the mouth of the porch lies
   * along z = -26.85, so a gate any further south would put you either into the
   * side of the porch or lengthwise along a sleeping LAN player. This one comes
   * out north of both, onto the approach the yard always used: round the front
   * of the porch, then over him and down into the doorway.
   *
   * And it is set where the line through the strip actually arrives rather than
   * where the middle of the fence is. A gate whose north post is on the racing
   * line is a gate every run scrapes on the way through.
   */
  from: -27.6,
  to: -25.0,
} as const;

export const VERAJA_POST: [number, number] = [VERAJA.x, (VERAJA.from + VERAJA.to) / 2];

/**
 * ==========================================================================
 *  KLAPITELINE
 * ==========================================================================
 *  A pile of split wood on the ground is a brown box, and a brown box on a
 *  yard full of brown boxes is not something a runner going past at 30 km/h
 *  ever notices. This is the thing people actually keep firewood in: a black
 *  steel rack with a canopy over it, stacked with the ends of the billets
 *  facing out - which is the side the corridor along the tub's north rim runs
 *  past, so what you see from the line is a wall of sawn ends under a roof.
 *
 *  That is the whole job. `F` only works within `KLAPI.reach` of it, so the
 *  rack has to be recognisable from far enough back that a runner has already
 *  decided which way round the palju they are going before they get there.
 *
 *  The collider is the stack. The frame, the canopy and the forty-odd billets
 *  are decor - the fingerprint never sees them, so the rack can be redrawn
 *  without retiring a leaderboard time.
 * ==========================================================================
 */
export const TELINE = {
  minX: 116.5,
  maxX: 119.5,
  /** Against the tub's rim, and only a klapi deep: these are 45 cm billets. */
  backZ: -19.85,
  depth: 0.45,
  /** How high the wood is stacked, and where the canopy sits over it. */
  stack: 1.15,
  postHeight: 1.78,
  post: 0.07,
} as const;

export function klapiteline(): { solids: SolidSpec[]; decor: DecorSpec[] } {
  const frontZ = TELINE.backZ + TELINE.depth;
  const parts: DecorSpec[] = [];

  // The frame: four uprights, a rail round the top of them, and a kick rail at
  // the bottom that the bottom course of wood sits on.
  for (const x of [TELINE.minX, TELINE.maxX - TELINE.post]) {
    for (const z of [TELINE.backZ, frontZ - TELINE.post]) {
      parts.push(
        decor(
          `klapiteline-tolppa-${parts.length + 1}`,
          'metal',
          x,
          z,
          x + TELINE.post,
          z + TELINE.post,
          TELINE.postHeight,
        ),
      );
    }
  }
  for (const [name, y] of [
    ['ala', 0.02],
    ['yla', TELINE.stack + 0.06],
  ] as [string, number][]) {
    for (const [side, z] of [
      ['taka', TELINE.backZ],
      ['etu', frontZ - TELINE.post],
    ] as [string, number][]) {
      parts.push(
        decor(
          `klapiteline-kisko-${name}-${side}`,
          'metal',
          TELINE.minX,
          z,
          TELINE.maxX,
          z + TELINE.post,
          0.06,
          y,
        ),
      );
    }
  }

  // The canopy. A klapiteline without a roof is a shelf; with one it is
  // unmistakable from the far end of the yard.
  parts.push(
    decor('klapiteline-katos', 'metal', TELINE.minX - 0.12, TELINE.backZ - 0.14, TELINE.maxX + 0.12, frontZ + 0.14, 0.05, TELINE.postHeight - 0.05),
    decor('klapiteline-harja', 'metal', TELINE.minX - 0.12, TELINE.backZ + TELINE.depth / 2 - 0.06, TELINE.maxX + 0.12, TELINE.backZ + TELINE.depth / 2 + 0.06, 0.07, TELINE.postHeight),
  );

  // And the wood. Courses of split billets, ends out, offset course by course
  // so the face reads as stacked rather than as tiling.
  //
  // Three things make it read as wood at a run rather than as a brown slab, and
  // all three are the same idea: a stack is *uneven*. The gap between billets
  // has to be wide enough to see, the sawn ends have to be paler than the bark
  // beside them, and no two of them may stand out of the face by the same
  // amount. The variation is drawn once at module load from a fixed seed
  // (`src/utils/prng.ts`), so it is the same stack on every machine and in
  // every replay - see README, *Determinismi*.
  const rng = mulberry32(0x1c1a97);
  const courseHeight = 0.2;
  const courses = Math.floor((TELINE.stack - 0.06) / courseHeight);
  /** Sawn end, split face, bark. Never two of the same next to each other. */
  const faces: MaterialKey[] = ['table', 'furniture', 'fence'];
  let n = 0;
  let previous = -1;
  for (let course = 0; course < courses; course++) {
    const y = 0.05 + course * courseHeight;
    const stride = 0.34;
    const offset = (course % 2) * (stride / 2) + rng() * 0.03;
    for (let x = TELINE.minX + 0.05 + offset; x < TELINE.maxX - 0.2; x += stride) {
      n++;
      let face = Math.floor(rng() * faces.length);
      if (face === previous) face = (face + 1) % faces.length;
      previous = face;
      // How far this one is pulled back into the rack, and how tall it is.
      const inset = 0.02 + rng() * 0.09;
      parts.push(
        decor(
          `klapi-${n}`,
          faces[face],
          x,
          TELINE.backZ + 0.01,
          Math.min(x + stride - 0.09, TELINE.maxX - 0.05),
          frontZ - 0.02 - inset,
          courseHeight - 0.04 - rng() * 0.02,
          y,
        ),
      );
    }
  }

  return {
    solids: [
      {
        // Collision only. The rack you see is the frame, the canopy and the
        // billets above; a box drawn here would cover every one of them and
        // leave a brown slab in a steel frame.
        ...solid('klapiteline', 'wood', TELINE.minX, TELINE.backZ, TELINE.maxX, frontZ, TELINE.stack),
        shape: 'invisible',
      },
    ],
    decor: parts,
  };
}

export const KLAPITELINE = klapiteline();

/**
 * ==========================================================================
 *  PITKÄ PÖYTÄ JA PENKIT
 * ==========================================================================
 *  The long table on the navetta's own gravel apron, with a bench down each
 *  side - the one every yard like this has, and the one everybody sat at last
 *  night. What is still on it says when they got up and what kind of night it
 *  had been: an ashtray nobody emptied, three snus tins, two cigarette packets,
 *  and down at the east end a bank card with a rolled seteli next to it.
 *
 *  It was put here for a reason, and the reason was the veräjä. Skipping the
 *  klapi used to be a straight run down the apron from the hole at the north
 *  end of the palju's fence, and a toll nobody pays is not a toll; the table
 *  went across that run, hard against the fence at its west end, so the way
 *  past it was round the east end and back.
 *
 *  The hole is boarded up now (`AIDAN_POHJOISPAA`) and nothing comes down the
 *  apron from the north any more, so what the group does today is what it
 *  looks like: it is the yard's furniture, and it keeps the apron the veräjä
 *  opens onto from being an empty square somebody arcs across. It stays where
 *  it is because moving it would retire every leaderboard time to tidy up a
 *  table that is standing exactly where a table stands.
 *
 *  ------------------------------------------------------------------------
 *   IT IS A WALL, NOT A HURDLE
 *  ------------------------------------------------------------------------
 *  Both benches and the table are colliders, and that is what stops it from
 *  becoming a third line. A single leap would have to stay above the table top
 *  for the group's whole 2.3 m depth plus the player's own width, which is a
 *  higher apex than a full vauhti meter buys; anything less is a bench, a
 *  table and a bench, and three take-offs is `MOMENTUM.jumpFactor` cubed. The
 *  cheapest way past a table is round it, which is also the true one.
 *
 *  How far east it reaches used to be tuned rather than chosen - the way round
 *  had to cost about a second more than the klapi did. There is no way round
 *  to price any more, so `PITKA_POYTA.toX` is now just how long a table this
 *  yard seats.
 * ==========================================================================
 */
export const PITKA_POYTA = {
  /** From the palju's fence eastwards, so there is no getting past the west end. */
  fromX: 125.75,
  toX: 131.2,
  /** Centre line of the table, on the apron north of the veräjä's exit. */
  z: -21.4,
  topWidth: 0.95,
  topHeight: 0.78,
  /** A bench each side, out far enough to sit at and close enough to be one thing. */
  benchGap: 0.22,
  benchWidth: 0.4,
  benchHeight: 0.45,
} as const;

function pitkaPoyta(): { solids: SolidSpec[]; decor: DecorSpec[]; cylinders: CylinderSpec[] } {
  const p = PITKA_POYTA;
  const half = p.topWidth / 2;
  const benchFrom = half + p.benchGap;
  const solids: SolidSpec[] = [
    solid('pitka-poyta', 'table', p.fromX, p.z - half, p.toX, p.z + half, p.topHeight),
  ];
  const decor: DecorSpec[] = [];
  const cylinders: CylinderSpec[] = [];

  for (const [side, name] of [
    [-1, 'etela'],
    [1, 'pohjoinen'],
  ] as [number, string][]) {
    const near = p.z + side * benchFrom;
    const far = near + side * p.benchWidth;
    solids.push(
      solid(
        `pitkan-poydan-penkki-${name}`,
        'wood',
        p.fromX,
        Math.min(near, far),
        p.toX,
        Math.max(near, far),
        p.benchHeight,
      ),
    );
  }

  // Legs, so the thing reads as a table and not as a plank on the gravel. Two
  // trestles per four metres, which is what a table this long actually needs.
  const legs = Math.max(2, Math.round((p.toX - p.fromX) / 2.2));
  for (let i = 0; i < legs; i++) {
    const x = p.fromX + 0.5 + ((p.toX - p.fromX - 1.0) * i) / (legs - 1);
    decor.push(
      decor2(`pitkan-poydan-jalka-${i + 1}`, 'furniture', x - 0.06, p.z - half + 0.1, x + 0.06, p.z + half - 0.1, p.topHeight - 0.06),
    );
  }

  // And what is still on it, from the far end of the table towards the fence:
  // the ashtray in the middle where it always is, snus tins beside it, and two
  // packets somebody left face down.
  const mid = (p.fromX + p.toX) / 2;
  cylinders.push(
    cylinder('tuhkakuppi', 'metal', mid, p.z, 0.075, 0.045, p.topHeight, { radialSegments: 10 }),
  );
  [-1.1, -0.75, 0.9].forEach((offset, i) => {
    cylinders.push(
      cylinder(
        `nuuskapurkki-${i + 1}`,
        i === 1 ? 'lamp' : 'porcelain',
        mid + offset,
        p.z + (i === 2 ? 0.2 : -0.18),
        0.035,
        0.022,
        p.topHeight,
        { radialSegments: 8 },
      ),
    );
  });
  [
    [mid + 0.45, p.z + 0.16, 'stain'],
    [mid - 1.6, p.z - 0.1, 'lamp'],
  ].forEach(([x, z, material], i) => {
    decor.push(
      decor2(
        `tupakka-aski-${i + 1}`,
        material as MaterialKey,
        (x as number) - 0.028,
        (z as number) - 0.045,
        (x as number) + 0.028,
        (z as number) + 0.045,
        0.023,
        p.topHeight,
      ),
    );
  });

  // And the other half of what last night was, down at the east end where the
  // last two sat: a card lying flat and a seteli rolled tight beside it. It is
  // left as two props rather than spelled out, because a table that has to be
  // stopped at and read is a table nobody running past ever sees.
  const raha = mid + 1.55;
  decor.push(
    // Four millimetres rather than a card's real 0.8: at that thickness the top
    // face and the table top are the same plane as far as the depth buffer is
    // concerned, and a card that flickers is worse than a card that is thick.
    decor2('luottokortti', 'monitor', raha - 0.043, p.z + 0.013, raha + 0.043, p.z + 0.067, 0.004, p.topHeight),
    // Pale, because what shows on a note rolled that tight is the edge of the
    // paper rather than the print. Fatter and a good deal shorter than the
    // tupakat in the aski next to it, which is the whole of the difference
    // between the two at the distance this is ever seen from.
    decor2('seteli', 'porcelain', raha - 0.038, p.z - 0.176, raha + 0.038, p.z - 0.164, 0.012, p.topHeight),
  );

  return { solids, decor, cylinders };
}

export const POYTARYHMA = pitkaPoyta();
